import { useEffect, useState } from "react";
import styled from "styled-components";
import { QuestionType } from "../../../types/Question";
import { AnswerValueType } from "../../../types/Answer";
import Question from "../../molecules/Question/Question";
import { postAnswers } from "../../../services/questionnaire.service";
import { setSequenceComponentDone } from "../../../services/sequence.service";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/ConfigContext";
import { useResonanceData } from "../ResonanceContext/ResonanceDataContext";
import useToast from "../../../hooks/useToast";
import {
  formatAnswerForPost,
  hasAsweredFunctionsByType,
} from "../../../helper/answer";
import { calculateQuestionnaireScore } from "../../../helper/questionnaire";
import { QuestionnaireDetailsType } from "../../../types/Questionnaire";
import { Dependency } from "../../../types/Dependency";

interface InlineQuestionProps {
  question: QuestionType;
  questionnaireId: number;
  userSequenceId: number;
  componentId: number;
  questionnaireData: QuestionnaireDetailsType;
  dependencies?: Dependency[];
  isScored?: boolean;
  initialValue?: AnswerValueType | null;
  autoSubmit?: boolean;
  cardMargin?: string;
}

const InlineQuestion = ({
  question,
  questionnaireId,
  userSequenceId,
  componentId,
  questionnaireData,
  dependencies = [],
  isScored = true,
  initialValue = null,
  autoSubmit = true,
  cardMargin = "0px",
}: InlineQuestionProps) => {
  const { study } = useConfig();
  const { user } = useAuth();
  const present = useToast();
  const { updateScore, updateSequenceComponentDone, updateSequenceDone } =
    useResonanceData();

  const [value, setValue] = useState<AnswerValueType | null>(
    Array.isArray(initialValue) ? initialValue : initialValue ?? null
  );

  useEffect(() => {
    if (
      initialValue !== null &&
      initialValue !== undefined &&
      (value === null || (Array.isArray(value) && value.length === 0))
    ) {
      setValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (val: AnswerValueType) => {
    setValue(val);

    if (autoSubmit) {
      saveAnswer(val).catch(() => {
        present("Error saving answer");
      });
    }
  };

  const saveAnswer = async (val: AnswerValueType | null) => {
    try {
      if (val === null || val === undefined) return;

      const body = formatAnswerForPost(
        question,
        val,
        Number(question.version ?? 1)
      );

      await postAnswers(
        {
          studyId: study.id,
          userSequenceId,
          questionnaireId,
          questionId: question.id,
        },
        body
      );

      const totalScore = calculateQuestionnaireScore(
        questionnaireData.questions,
        [
          {
            questionId: question.id,
            questionType: question.type,
            value: val,
          },
        ],
        questionnaireData.is_scored ?? isScored
      );

      const { data } = await setSequenceComponentDone(
        {
          studyId: study.id,
          userId: user.id,
          sequenceId: userSequenceId.toString(),
          sequenceComponentId: componentId,
        },
        totalScore
      );

      if (questionnaireData.is_scored && totalScore !== null) {
        updateScore(
          data.userSequenceComponentId,
          data.userSequenceId,
          data.sequenceId,
          data.sequenceComponentId,
          data.questionnaireId,
          data.score
        );
      }

      updateSequenceComponentDone(
        data.userSequenceId,
        data.sequenceComponentId
      );

      if (data.allDone) {
        updateSequenceDone(data.userSequenceId);
      }
    } catch (err: any) {
      present(err?.response?.data?.message || "Error saving answer");
    }
  };

  return (
    <Wrapper>
      <Question
        question={question}
        answer={value}
        onAnswerChange={handleChange}
        cardMargin={cardMargin}
      />

      {!autoSubmit && (
        <SubmitButton type="button" onClick={() => saveAnswer(value)}>
          Submit
        </SubmitButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  max-width: 340px;
  margin: 0 auto;
`;

const SubmitButton = styled.button`
  margin-top: 10px;
  padding: 10px 16px;
  border-radius: 8px;
  background-color: #2db6b4;
  color: white;
  border: none;
  font-weight: 500;
`;

export default InlineQuestion;